import React, { useState, useEffect } from 'react';
import { Alert, Card, Button, Modal, Form, Input, Select, InputNumber, Switch, message } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import {
  routeApi,
  channelApi,
  transportModeApi,
  carrierApi,
  type RouteDTO,
  type ChannelDTO,
  type TransportModeDTO,
  type CarrierDTO,
} from '../api/kfc';
import { COUNTRY_LIST, formatCountryLabel } from '../constants/countries';
import { clientTablePagination } from '../utils/tablePagination';

const SERVICE_TYPE_OPTIONS = [
  { label: '专线 (D2D)', value: 'D2D' },
  { label: '港到港 (P2P)', value: 'P2P' },
];

const serviceTypeLabel = (v?: string) => {
  if (v === 'P2P') return '港到港';
  if (v === 'D2D' || !v) return '专线';
  return v;
};

const RouteManage: React.FC = () => {
  const [list, setList] = useState<RouteDTO[]>([]);
  const [channels, setChannels] = useState<ChannelDTO[]>([]);
  const [transportModes, setTransportModes] = useState<TransportModeDTO[]>([]);
  const [carriers, setCarriers] = useState<CarrierDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    routeApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载线路列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    channelApi.list().then((res) => setChannels(res.data)).catch(() => setChannels([]));
    transportModeApi.list(true).then((res) => setTransportModes(res.data)).catch(() => setTransportModes([]));
    carrierApi.list(true).then((res) => setCarriers(res.data)).catch(() => setCarriers([]));
  }, []);

  const modeLabel = (mode?: string) =>
    transportModes.find((t) => t.mode === mode)?.displayName || mode || '-';

  const carrierLabel = (r: RouteDTO) => {
    if (r.carrierCode || r.carrierName) {
      return [r.carrierCode, r.carrierName].filter(Boolean).join(' - ');
    }
    return r.carrierId || '-';
  };

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, priceCoefficient: 1, serviceType: 'D2D' });
    setModalOpen(true);
  };

  const openEdit = (r: RouteDTO) => {
    setEditingId(r.id);
    form.setFieldsValue({
      serviceType: r.serviceType || 'D2D',
      routeCode: r.routeCode,
      transportMode: r.transportMode,
      carrierId: r.carrierId,
      channelIds: r.channelIds ?? [],
      originCountryCode: r.origin?.countryCode,
      originCityName: r.origin?.cityName,
      destinationCountryCode: r.destination?.countryCode,
      destinationCityName: r.destination?.cityName,
      transitTime: r.transitTime,
      priceCoefficient: r.priceCoefficient,
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, unknown>) => {
    const originCountry = COUNTRY_LIST.find((c) => c.code === values.originCountryCode);
    const destCountry = COUNTRY_LIST.find((c) => c.code === values.destinationCountryCode);
    const hasOrigin = !!(values.originCountryCode || values.originCityName);
    const hasDest = !!(values.destinationCountryCode || values.destinationCityName);
    const payload: Partial<RouteDTO> = {
      serviceType: values.serviceType as string,
      routeCode: values.routeCode as string,
      transportMode: values.transportMode as string,
      carrierId: values.carrierId as string,
      channelIds: values.channelIds as string[],
      origin: hasOrigin
        ? {
            countryName: originCountry?.nameEn,
            countryCode: values.originCountryCode as string | undefined,
            cityName: values.originCityName as string | undefined,
          }
        : undefined,
      destination: hasDest
        ? {
            countryName: destCountry?.nameEn,
            countryCode: values.destinationCountryCode as string | undefined,
            cityName: values.destinationCityName as string | undefined,
          }
        : undefined,
      transitTime: values.transitTime as string | undefined,
      priceCoefficient: values.priceCoefficient as number,
      isActive: values.isActive !== false,
    };
    const submit = editingId ? routeApi.update(editingId, payload) : routeApi.create(payload);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  return (
    <>
      <Card title="线路列表" extra={<Button type="primary" onClick={openAdd}>新增线路</Button>}>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="线路功能将逐步废弃，新建运单请直接选择国家/代理/渠道。"
        />
        <AdaptiveTable
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          scroll={{ x: 1100 }}
          columns={[
            {
              title: '服务类型',
              dataIndex: 'serviceType',
              width: 100,
              render: (v: string) => serviceTypeLabel(v),
            },
            { title: '线路编码', dataIndex: 'routeCode', width: 140 },
            {
              title: '运输方式',
              dataIndex: 'transportMode',
              width: 90,
              render: (v: string) => modeLabel(v),
            },
            {
              title: '承运商',
              width: 140,
              ellipsis: true,
              render: (_: unknown, r: RouteDTO) => carrierLabel(r),
            },
            {
              title: '起运地',
              width: 160,
              render: (_: unknown, r: RouteDTO) =>
                r.origin ? `${r.origin.countryCode ?? ''} ${r.origin.cityName ?? ''}`.trim() || '-' : '-',
            },
            {
              title: '目的地',
              width: 160,
              render: (_: unknown, r: RouteDTO) =>
                r.destination
                  ? `${r.destination.countryCode ?? ''} ${r.destination.cityName ?? ''}`.trim() || '-'
                  : '-',
            },
            { title: '时效', dataIndex: 'transitTime', width: 80 },
            { title: '价格系数', dataIndex: 'priceCoefficient', width: 90 },
            { title: '启用', dataIndex: 'isActive', width: 60, render: (v: boolean) => (v ? '是' : '否') },
            {
              title: '操作',
              width: 80,
              fixed: 'right',
              render: (_, r: RouteDTO) => (
                <Button type="link" size="small" onClick={() => openEdit(r)}>
                  编辑
                </Button>
              ),
            },
          ]}
        />
      </Card>
      <Modal title={editingId ? '编辑线路' : '新增线路'} open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} width={560}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="serviceType" label="服务类型" rules={[{ required: true, message: '请选择服务类型' }]}>
            <Select options={SERVICE_TYPE_OPTIONS} placeholder="请选择服务类型" />
          </Form.Item>
          <Form.Item name="routeCode" label="线路编码" rules={[{ required: true, message: '请输入线路编码' }]}>
            <Input disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="transportMode" label="运输方式" rules={[{ required: true, message: '请选择运输方式' }]}>
            <Select
              options={transportModes.map((t) => ({
                label: t.displayName || t.mode,
                value: t.mode,
              }))}
            />
          </Form.Item>
          <Form.Item name="carrierId" label="承运商" rules={[{ required: true, message: '请选择承运商' }]}>
            <Select
              placeholder="请选择承运商"
              options={carriers.map((c) => ({
                label: `${c.carrierCode}${c.carrierName ? ` - ${c.carrierName}` : ''}`,
                value: c.id,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="channelIds"
            label="渠道"
            rules={[{ required: true, type: 'array', min: 1, message: '请至少选择一个渠道' }]}
          >
            <Select
              mode="multiple"
              placeholder="选择渠道"
              options={channels.map((c) => ({
                label: `${c.channelCode}${c.channelName ? ` (${c.channelName})` : ''}`,
                value: c.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="originCountryCode" label="起运地-国家">
            <Select
              allowClear
              placeholder="可选"
              showSearch
              optionFilterProp="label"
              options={COUNTRY_LIST.map((c) => ({ label: formatCountryLabel(c), value: c.code }))}
            />
          </Form.Item>
          <Form.Item name="originCityName" label="起运地-城市">
            <Input placeholder="可选" />
          </Form.Item>
          <Form.Item name="destinationCountryCode" label="目的地-国家">
            <Select
              allowClear
              placeholder="可选"
              showSearch
              optionFilterProp="label"
              options={COUNTRY_LIST.map((c) => ({ label: formatCountryLabel(c), value: c.code }))}
            />
          </Form.Item>
          <Form.Item name="destinationCityName" label="目的地-城市">
            <Input placeholder="可选" />
          </Form.Item>
          <Form.Item name="transitTime" label="时效">
            <Input placeholder="如 5-7工作日" />
          </Form.Item>
          <Form.Item name="priceCoefficient" label="价格系数" initialValue={1}>
            <InputNumber min={0.01} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RouteManage;
